import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dices, Camera, GitCompare, FileDown, UserPlus, Lock, ChevronRight } from 'lucide-react';
import ModalDrawer from './ModalDrawer.js';
import { buildCheckoutUrl, isBillingConfigured, rememberPostAuthPaywall, FREE_LIMITS } from '../config/billing';
import { trackEvent } from '../hooks/useConsent';
import { useOffer } from '../hooks/useOffer';

// The single reusable upgrade dialog. `source` says which gate opened it and
// is forwarded to analytics only. Checkout is Stripe-hosted (Payment Links):
// we hand over the Firebase uid as client_reference_id and the webhook flips
// the plan — the card never touches Fiwara.
const PaywallModal = ({ isOpen, onClose, source, theme, isDemoMode, currentUser, onCreateAccount, onOpenPlan }) => {
    const { t } = useTranslation();
    const configured = isBillingConfigured();
    // L5 — same source of truth as the Plan page and the landing card.
    const offer = useOffer();
    // The gate's own name, when there is a phrase for it. An unknown source
    // says nothing rather than a placeholder.
    // `runs` and `snapshots` are read by their own lines; each free cap is stated
    // in one place (FREE_LIMITS) rather than typed into the copy, so lowering it
    // cannot leave this sentence quoting the old number.
    const sourceLabel = source
        ? t(`billing.paywall.from.${source}`, { defaultValue: '', runs: FREE_LIMITS.monteCarloRuns, snapshots: FREE_LIMITS.snapshots })
        : '';

    const features = [
        { icon: <Dices size={18} />, label: t('billing.paywall.features.monteCarlo') },
        { icon: <Camera size={18} />, label: t('billing.paywall.features.snapshots') },
        { icon: <GitCompare size={18} />, label: t('billing.paywall.features.compare'), soon: true },
        { icon: <FileDown size={18} />, label: t('billing.paywall.features.export'), soon: true },
    ];

    const [leavingFor, setLeavingFor] = useState(null);
    // Back from Stripe restores this page from the bfcache, button still locked
    useEffect(() => {
        const onShow = (e) => { if (e.persisted) setLeavingFor(null); };
        window.addEventListener('pageshow', onShow);
        return () => window.removeEventListener('pageshow', onShow);
    }, []);
    const startCheckout = (planKey) => {
        if (!configured || !currentUser?.uid || isDemoMode) return;
        trackEvent('checkout_click', { plan: planKey, source: source || 'unknown', offer: offer.active ? 'founder' : 'standard' });
        // At click time — the promo param must reflect the seat count now, not
        // when this drawer was rendered.
        const url = buildCheckoutUrl(planKey, currentUser.uid, currentUser.email || undefined, { taken: offer.taken });
        // Stripe takes a moment to load; say so, and stop a second click
        if (url) { setLeavingFor(planKey); window.location.assign(url); }
    };

    // Annotation pass 1: the drawer prices Pro; the Plan page explains it. This
    // is the way from one to the other, rather than a second, longer drawer.
    const handleLearnMore = () => {
        onClose();
        if (onOpenPlan) onOpenPlan();
    };

    const handleDemoCreateAccount = () => {
        // Reopen the paywall once the fresh account reaches its dashboard.
        rememberPostAuthPaywall(source || 'demo');
        onClose();
        if (onCreateAccount) onCreateAccount();
    };

    return (
        <ModalDrawer
            isOpen={isOpen}
            onClose={onClose}
            title={t('billing.paywall.title')}
            theme={theme}
            drawerId="paywall"
            footer={
                <button className="btn btn-ghost" onClick={handleLearnMore}>
                    <span>{t('billing.paywall.learnMore')}</span>
                    <ChevronRight size={16} />
                </button>
            }
        >
            <p className="sub" style={{ marginBottom: 16 }}>{t('billing.paywall.subtitle')}</p>

            {/* Which gate opened this, said once. Eight gates share one drawer,
                and the line at the top is the only part that differs between
                them — eight per-feature dialogs would be eight things to keep
                honest instead of one. */}
            {source && sourceLabel && (
                <div className="pw-from">
                    <Lock size={15} />
                    <span><b>{sourceLabel}</b></span>
                </div>
            )}

            <ul className="pw-features">
                {features.map((feature, idx) => (
                    <li key={idx}>
                        <span className="ico">{feature.icon}</span>
                        {feature.label}
                        {feature.soon && <em className="pw-soon">{t('billing.paywall.soon')}</em>}
                    </li>
                ))}
            </ul>

            {isDemoMode ? (
                <div className="pw-demo">
                    <p className="pw-demo-note">{t('billing.paywall.demoNote')}</p>
                    <button className="btn btn-grad btn-block" onClick={handleDemoCreateAccount}>
                        <UserPlus size={18} />
                        <span>{t('billing.paywall.demoCta')}</span>
                    </button>
                </div>
            ) : (
                <div className={`pw-plans ${configured ? '' : 'off'}`}>
                    <button className="pw-plan primary" onClick={() => startCheckout('yearly')} disabled={!configured || !!leavingFor} aria-busy={leavingFor === 'yearly'}>
                        {leavingFor === 'yearly' && <span className="spinner pw-spinner" aria-hidden="true" />}
                        <span className="pw-badge">
                            {offer.active ? t('billing.paywall.yearly.badge') : t('billing.paywall.yearly.standardBadge')}
                        </span>
                        <span className="pw-price">
                            {offer.active ? t('billing.paywall.yearly.price') : t('billing.paywall.yearly.standardPrice')}
                            <em>{t('billing.paywall.yearly.period')}</em>
                        </span>
                        <span className="pw-note">
                            {offer.active ? t('billing.paywall.yearly.note') : t('billing.paywall.yearly.standardNote')}
                        </span>
                        {offer.showCounter && (
                            <span className="pw-note">
                                {t('billing.offer.remaining', { remaining: offer.remaining, limit: offer.limit })}
                            </span>
                        )}
                    </button>
                    <button className="pw-plan" onClick={() => startCheckout('monthly')} disabled={!configured || !!leavingFor} aria-busy={leavingFor === 'monthly'}>
                        {leavingFor === 'monthly' && <span className="spinner pw-spinner" aria-hidden="true" />}
                        <span className="pw-price">
                            {t('billing.paywall.monthly.price')}
                            <em>{t('billing.paywall.monthly.period')}</em>
                        </span>
                        <span className="pw-note">{t('billing.paywall.monthly.note')}</span>
                    </button>
                    {/* Stripe bills these VAT-exclusive — €49 plus the buyer's
                        local rate. Said once under both plans rather than twice
                        inside them, so the two buttons stay scannable. */}
                    <p className="pw-vat">{t('billing.vatNote')}</p>
                    {!configured && <p className="pw-unconfigured">{t('billing.paywall.notConfigured')}</p>}
                </div>
            )}

            <p className="pw-smallprint">
                {t('billing.paywall.checkoutNote')} {t('billing.paywall.cancelNote')}{' '}
                <a href="/terms.html" target="_blank" rel="noopener noreferrer">{t('landing.footer.terms')}</a>
                {' · '}
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer">{t('landing.footer.privacy')}</a>
            </p>
        </ModalDrawer>
    );
};

export default PaywallModal;
